import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, ErrorNote } from '../ui';

interface JobRow { id: string; status: string; contentItemId: string | null }
interface ContentRow { id: string; state: string; contentType: string; title: string | null; schedules: { publishAt: string; status: string; method: string }[] }

const CONTENT_GROUPS: { label: string; states: string[]; tone: string }[] = [
  { label: 'In progress', tone: 'text-brand-300', states: ['IDEA', 'RESEARCHING', 'RESEARCH_COMPLETE', 'GENERATING_TITLE', 'TITLE_READY', 'GENERATING_SCRIPT', 'SCRIPT_READY', 'GENERATING_REFERENCE_IMAGE', 'REFERENCE_IMAGE_READY', 'GENERATING_MEDIA', 'MEDIA_READY', 'GENERATING_CAPTION', 'CAPTION_READY', 'VALIDATING'] },
  { label: 'Awaiting approval', tone: 'text-amber-300', states: ['REVIEW_REQUIRED'] },
  { label: 'Scheduled', tone: 'text-emerald-300', states: ['APPROVED', 'SCHEDULED', 'PUBLISHING', 'FACEBOOK_PROCESSING'] },
  { label: 'Published', tone: 'text-emerald-400', states: ['PUBLISHED'] },
  { label: 'Problems', tone: 'text-red-300', states: ['RETRY_WAIT', 'BLOCKED', 'FAILED'] },
];

export function PageOverviewScreen({ pageId, onNavigate }: { pageId: string; onNavigate?: (screen: string) => void }) {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [content, setContent] = useState<ContentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      setJobs((await window.pagepilot.jobs.list('ALL', pageId)) as JobRow[]);
      setContent((await window.pagepilot.content.list(pageId)) as ContentRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [pageId]);

  const q = {
    running: jobs.filter((j) => j.status === 'RUNNING' || j.status === 'CLAIMED').length,
    waiting: jobs.filter((j) => j.status === 'PENDING' || j.status === 'RETRY_WAIT').length,
    problem: jobs.filter((j) => j.status === 'FAILED' || j.status === 'BLOCKED').length,
  };

  const upcoming = content
    .flatMap((c) => c.schedules.filter((s) => s.status !== 'DONE' && new Date(s.publishAt).getTime() > Date.now()).map((s) => ({ title: c.title, type: c.contentType, ...s })))
    .sort((a, b) => new Date(a.publishAt).getTime() - new Date(b.publishAt).getTime())
    .slice(0, 6);

  const generate = async (type: 'IMAGE_POST' | 'REEL') => {
    setBusy(type);
    setError(null);
    try { await window.pagepilot.content.generateNow(pageId, type); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      {error && <ErrorNote message={error} />}

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <p className="text-xs text-neutral-500">Queue</p>
          <p className="mt-1 text-2xl font-semibold text-neutral-100">{q.running + q.waiting}</p>
          <p className="mt-1 text-xs text-neutral-400">{q.running} running · {q.waiting} waiting{q.problem ? ` · ` : ''}{q.problem > 0 && <span className="text-red-400">{q.problem} problem</span>}</p>
        </Card>
        <Card>
          <p className="text-xs text-neutral-500">Posts</p>
          <p className="mt-1 text-2xl font-semibold text-neutral-100">{content.length}</p>
          <p className="mt-1 text-xs text-neutral-400">{content.filter((c) => c.state === 'PUBLISHED').length} published · {content.filter((c) => c.state === 'REVIEW_REQUIRED').length} awaiting approval</p>
        </Card>
        <Card>
          <p className="text-xs text-neutral-500">Generate now</p>
          <div className="mt-2 flex gap-2">
            <Button disabled={busy !== null} onClick={() => void generate('IMAGE_POST')}>{busy === 'IMAGE_POST' ? '…' : 'Image post'}</Button>
            <Button variant="ghost" disabled={busy !== null} onClick={() => void generate('REEL')}>{busy === 'REEL' ? '…' : 'Reel'}</Button>
          </div>
        </Card>
      </div>

      <Card title="Content pipeline">
        <div className="grid grid-cols-5 gap-3">
          {CONTENT_GROUPS.map((g) => (
            <div key={g.label} className="rounded-lg border border-neutral-800 bg-neutral-950 p-3">
              <p className={`text-2xl font-semibold ${g.tone}`}>{content.filter((c) => g.states.includes(c.state)).length}</p>
              <p className="mt-1 text-[11px] text-neutral-500">{g.label}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Next scheduled">
        {upcoming.length === 0 ? (
          <p className="text-sm text-neutral-500">Nothing scheduled yet. Generate a post or wait for the daily generation time.</p>
        ) : (
          <ul className="divide-y divide-neutral-800/60 text-sm">
            {upcoming.map((u, i) => (
              <li key={i} className="flex items-center justify-between py-2">
                <span className="min-w-0 truncate text-neutral-200">{u.title ?? 'Untitled'}</span>
                <span className="ml-3 flex shrink-0 items-center gap-2 text-xs text-neutral-500">
                  <Badge status="HEALTHY" label={u.type === 'REEL' ? 'reel' : 'image'} />
                  {new Date(u.publishAt).toLocaleString()} · {u.method === 'FACEBOOK_NATIVE' ? 'FB native' : 'PagePilot'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {onNavigate && <div className="mt-3"><Button variant="ghost" onClick={() => onNavigate('calendar')}>Open calendar</Button></div>}
      </Card>
    </div>
  );
}
